import React, { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import NoAccessPage from "./NoAccessPage";
import IsLoading from "../components/IsLoading";
import HasError from "../components/HasError";
import { fetchStations } from "../store/stations";
import { fetchStacks } from "../store/stacks";
import { fetchSound } from "../store/sound";
import { MdCheckBox } from "react-icons/md";
import { MdCheckBoxOutlineBlank } from "react-icons/md";
import { RiRadioButtonLine } from "react-icons/ri";
import GoToStationButton from "../components/GoToStationButton";
import { useNavigate } from "react-router-dom";
import { fetchWeather } from "../store/weather";

const Information = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isCompany = useSelector((state) => state.login.isCompany);
  const stations = useSelector((state) => state.stations);
  const stationsLoading = stations.stationsLoading;
  const stationsError = stations.stationsError;
  const SAPA = stations.stationsData.SAPA;
  const ESPS = stations.stationsData.ESPS;
  const OSPM = stations.stationsData.OSPM;
  const SATA = stations.stationsData.SATA;
  const stacks = useSelector((state) => state.stacks);
  const stacksLoading = stacks.stacksLoading;
  const stacksError = stacks.stacksError;
  const OPSGA = stacks.stacksData?.OPSGA || [];
  const OPEDA = stacks.stacksData?.OPEDA || [];
  const sound = useSelector((state) => state.sound);
  const OSPL = sound?.soundData?.OSPL || [];
  const weather = useSelector((state) => state.weather);
  const SATA2 = weather?.weatherData?.SATA2;
  const soundLoading = sound.soundLoading;
  const soundError = sound.soundError;
  const information = useSelector((state) => state.information);
  const informationData = information.informationData;
  const informationError = information.informationError;
  const informationLoading = information.informationLoading;
  const [selectedDevice, setSelectedDevice] = useState("all");
  const [selectedProvince, setSelectedProvince] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedAQMS, setSelectedAQMS] = useState("all");
  const [selectedOPM, setSelectedOpm] = useState("all");
  const [searchTerm, setSearchTerm] = useState(null);

  const GetInformationOfTheDevice = ({ deviceType, deviceId }) => {
    // Find the specific station, or default to an empty object
    const theSpecificStation =
      informationData.find(
        (x) =>
          x.device === String(deviceType) && x.device_id === Number(deviceId)
      ) || {};

    // Return the device information object
    return {
      device_id: theSpecificStation.device_id,
      device: theSpecificStation.device,
      is_kit: theSpecificStation.is_kit,
      is_test: theSpecificStation.is_test,
      is_aqms: theSpecificStation.is_aqms,
      is_opm: theSpecificStation.is_opm,
      user: theSpecificStation.user,
      province: theSpecificStation.province,
      city: theSpecificStation.city,
      stationName: theSpecificStation.stationName,
      longitude: theSpecificStation.longitude,
      latitude: theSpecificStation.latitude,
      slug: theSpecificStation.slug,
    };
  };

  // Function to modify devices based on their type
  const modifyDevices = (deviceArray, deviceType) => {
    if (deviceArray && Array.isArray(deviceArray)) {
      return deviceArray
        .map((element) => {
          const deviceInfo = GetInformationOfTheDevice({
            deviceType,
            deviceId: element.device_id,
          });

          // Only proceed if the device is not a test device
          if (!deviceInfo.is_test) {
            return { ...element, ...deviceInfo };
          }
          return null; // Return null for test devices
        })
        .filter(Boolean); // Remove any null values
    }
    return []; // Return an empty array if the input is not valid
  };

  // Modify all device types
  const modifiedSAPA = modifyDevices(SAPA, "SAPA");
  const modifiedSATA = modifyDevices(SATA, "SATA");
  const modifiedSATA2 = modifyDevices(SATA2, "SATA2");
  const modifiedESPS = modifyDevices(ESPS, "ESPS");
  const modifiedOSPM = modifyDevices(OSPM, "OSPM");
  const modifiedOSPL = modifyDevices(OSPL, "OSPL");
  const modifiedOPSGA = modifyDevices(OPSGA, "OPSGA");
  const modifiedOPEDA = modifyDevices(OPEDA, "OPEDA");

  const AllData = [].concat(
    modifiedSAPA,
    modifiedSATA,
    modifiedSATA2,
    modifiedESPS,
    modifiedOSPM,
    modifiedOSPL,
    modifiedOPSGA,
    modifiedOPEDA
  );

  useLayoutEffect(() => {
    const fetchData = () => {
      dispatch(fetchStations());
      dispatch(fetchSound());
      dispatch(fetchStacks());
      dispatch(fetchWeather());
    };

    fetchData();

    const intervalId = setInterval(fetchData, 1500000); // 1500000 ms = 25 minutes

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, [dispatch]);

  const navigateToStackHistory = (theRouteUrl) => {
    const navigateUrl = "/stack-data/" + theRouteUrl;
    navigate(navigateUrl);
  };

  const navigateToSoundHistory = (theRouteUrl) => {
    const navigateUrl = "/sound-data/" + theRouteUrl;
    navigate(navigateUrl);
  };

  const navigateToWeatherHistory = (theRouteUrl) => {
    const navigateUrl = "/SATA-history/" + theRouteUrl;
    navigate(navigateUrl);
  };

  const convertToJalali = (x) => {
    const A = x.slice(0, 10);
    const B = x.slice(11, 19);
    const C = new Date(A);
    const D = new Intl.DateTimeFormat("fa-IR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(C);
    return `${D} (${B})`;
  };

  const checkOnlineOrOffline = (x, y, z) => {
    const todayDate = new Date();
    const lastDataDate = new Date(x);
    const differenceInMilliseconds = todayDate - lastDataDate;
    const sixtyMinutesInMilliseconds = 60 * 60 * 1000;

    // If both AQI values are zero, return "grey"
    if (
      differenceInMilliseconds < sixtyMinutesInMilliseconds &&
      ((Number(y) === 0 && Number(z) === 0) ||
        (Number(y) === 500 && Number(z) === 500))
    ) {
      return "suspicious";
    }

    // If data is recent (within the last hour), return "online"
    if (differenceInMilliseconds < sixtyMinutesInMilliseconds) {
      return "online";
    }

    // Otherwise, return "red" (device offline)
    return "offline";
  };

  // Helper function to determine the icon color class based on status
  const getIconColor = (status) => {
    switch (status) {
      case "online":
        return "text-success"; // green
      case "suspicious":
        return "text-secondary"; // grey
      case "offline":
        return "text-danger"; // red
      default:
        return "";
    }
  };

  // function PersianSortComparer(a, b) {
  //   const provinceA = a.province || "";
  //   const provinceB = b.province || "";
  //   const cityA = a.city || "";
  //   const cityB = b.city || "";
  //   const stationNameA = a.stationName || "";
  //   const stationNameB = b.stationName || "";

  //   if (provinceA === provinceB) {
  //     if (cityA === cityB) {
  //       return stationNameA.localeCompare(stationNameB);
  //     } else {
  //       return cityA.localeCompare(cityB);
  //     }
  //   } else {
  //     return provinceA.localeCompare(provinceB);
  //   }
  // }

  const applyFilters = () => {
    let tempData = [...AllData];

    // Filter by search term
    if (searchTerm) {
      tempData = tempData.filter((station) => {
        const device = station.device ? station.device.toLowerCase() : "";
        const province = station.province ? station.province.toLowerCase() : "";
        const stationName = station.stationName
          ? station.stationName.toLowerCase()
          : "";
        const deviceId = station.device_id
          ? station.device_id.toString() // Convert number to string
          : "";
        const city = station.city ? station.city.toLowerCase() : "";

        return (
          device.includes(searchTerm.toLowerCase()) ||
          province.includes(searchTerm.toLowerCase()) ||
          stationName.includes(searchTerm.toLowerCase()) ||
          deviceId.includes(searchTerm) || // No need to call toLowerCase() on number
          city.includes(searchTerm.toLowerCase())
        );
      });
    }

    // Filter by selected device
    if (selectedDevice !== "all") {
      tempData = tempData.filter(
        (station) => station.device === selectedDevice
      );
    }

    // Filter by selected province
    if (selectedProvince !== "all") {
      tempData = tempData.filter(
        (station) => station.province === selectedProvince
      );
    }

    // Filter by selected OPM status
    if (selectedOPM !== "all") {
      tempData = tempData.filter((station) =>
        selectedOPM === "yes"
          ? station.is_opm === true
          : station.is_opm === false
      );
    }

    // Filter by selected AQMS status
    if (selectedAQMS !== "all") {
      tempData = tempData.filter((station) =>
        selectedAQMS === "yes"
          ? station.is_aqms === true
          : station.is_aqms === false
      );
    }

    // Filter Online & Offline devices & AQI === 0
    if (selectedStatus !== "all") {
      tempData = tempData.filter((station) => {
        const datetimeToCheck =
          station.device === "OSPL"
            ? station.created_datetime
            : station.ccreated_datetime;

        const status = checkOnlineOrOffline(
          datetimeToCheck,
          station.PM25AQI,
          station.PM10AQI
        );

        if (selectedStatus === "online") {
          return status === "online";
        } else if (selectedStatus === "offline") {
          return status === "offline";
        } else if (selectedStatus === "suspicious") {
          return status === "suspicious";
        }

        return false; // Default case if none of the statuses match
      });
    }

    return tempData;
  };

  const currentPageData = useMemo(() => {
    return applyFilters(); // Ensure applyFilters returns the filtered data
  }, [
    AllData,
    selectedDevice,
    selectedProvince,
    selectedOPM,
    selectedAQMS,
    selectedStatus,
    applyFilters,
  ]); // Add necessary dependencies to trigger recalculation

  useEffect(() => {}, [
    isCompany,
    stationsLoading,
    informationLoading,
    informationError,
    stationsError,
    soundLoading,
    soundError,
    stacksLoading,
    stacksError,
    modifiedSAPA?.length,
    modifiedSATA?.length,
    modifiedSATA2?.length,
    modifiedESPS?.length,
    modifiedOSPM?.length,
    modifiedOSPL?.length,
    modifiedOPSGA?.length,
    modifiedOPEDA?.length,
    currentPageData?.length,
    AllData?.length,
  ]);

  if (!isCompany) {
    return <NoAccessPage />;
  } else if (isCompany && (informationLoading || stationsLoading)) {
    return <IsLoading />;
  } else if (isCompany && (stationsError || informationError)) {
    return <HasError />;
  } else if (isCompany && currentPageData) {
    return (
      <div className="container-fluid bg-white px-3 py-5">
        <div className="row mx-auto d-flex justify-content-center align-items-center input-group bg-white p-1 my-2">
          <div className="col-auto d-flex align-items-center  my-1">
            <input
              className="mx-1 form-control"
              type="text"
              placeholder="جستجو..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="col-auto d-flex align-items-center  my-1">
            <label
              className="text-dark"
              htmlFor="device"
              style={{ whiteSpace: "nowrap" }}
            >
              دستگاه :
            </label>
            <select
              className="mx-1 form-select"
              name="device"
              id="device"
              onChange={(e) => setSelectedDevice(e.target.value)}
            >
              <option value="all">-</option>
              <option value="SATA">SATA</option>
              <option value="SATA2">SATA2</option>
              <option value="SAPA">SAPA</option>
              <option value="OSPM">OSPM</option>
              <option value="ESPS">ESPS</option>
              <option value="OSPL">OSPL</option>
              <option value="OPSGA">OPSGA</option>
            </select>
          </div>

          <div className="col-auto d-flex align-items-center">
            <label
              className="text-dark"
              htmlFor="province"
              style={{ whiteSpace: "nowrap" }}
            >
              استان :
            </label>
            <select
              className="mx-1 form-select"
              name="province"
              id="province"
              onChange={(e) => setSelectedProvince(e.target.value)}
            >
              <option value="all">-</option>
              <option value="آذربایجان شرقی"> آذربایجان شرقی</option>
              <option value="آذربایجان غربی">آذربایجان غربی</option>
              <option value="اردبیل">اردبیل</option>
              <option value="اصفهان">اصفهان</option>
              <option value="البرز">البرز</option>
              <option value="ایلام">ایلام</option>
              <option value="بوشهر">بوشهر</option>
              <option value="تهران">تهران</option>
              <option value="چهارمحال و بختیاری">چهارمحال و بختیاری</option>
              <option value="خراسان جنوبی">خراسان جنوبی</option>
              <option value="خراسان رضوی">خراسان رضوی</option>
              <option value="خراسان شمالی">خراسان شمالی</option>
              <option value="خوزستان">خوزستان</option>
              <option value="زنجان">زنجان</option>
              <option value="سمنان">سمنان</option>
              <option value="سیستان و بلوچستان">سیستان و بلوچستان</option>
              <option value="فارس">فارس</option>
              <option value="قزوین">قزوین</option>
              <option value="قم">قم</option>
              <option value="کردستان">کردستان</option>
              <option value="کرمان">کرمان</option>
              <option value="کرمانشاه">کرمانشاه</option>
              <option value="کهگیلویه و بویراحمد">کهگیلویه و بویراحمد</option>
              <option value="گلستان">گلستان</option>
              <option value="گیلان">گیلان</option>
              <option value="لرستان">لرستان</option>
              <option value="مازندران">مازندران</option>
              <option value="مرکزی">مرکزی</option>
              <option value="هرمزگان">هرمزگان</option>
              <option value="همدان">همدان</option>
              <option value="یزد">یزد</option>
            </select>
          </div>
          <div className="col-auto d-flex align-items-center">
            <label
              className="text-dark"
              htmlFor="sensor"
              style={{ whiteSpace: "nowrap" }}
            >
              وضعیت:
            </label>
            <select
              className="mx-1 form-select"
              name="sensor"
              id="sensor"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">-</option>
              <option value="online">آنلاین</option>
              <option value="offline">آفلاین</option>
              <option value="suspicious">مشکوک</option>
            </select>
          </div>
          <div className="col-auto d-flex align-items-center">
            <label
              className="text-dark"
              htmlFor="sensor"
              style={{ whiteSpace: "nowrap" }}
            >
              OPM:
            </label>
            <select
              className="mx-1 form-select"
              name="sensor"
              id="sensor"
              onChange={(e) => setSelectedOpm(e.target.value)}
            >
              <option value="all">-</option>
              <option value="yes">✔</option>
              <option value="no">X</option>
            </select>
          </div>
          <div className="col-auto d-flex align-items-center">
            <label
              className="text-dark"
              htmlFor="aqms"
              style={{ whiteSpace: "nowrap" }}
            >
              AQMS:
            </label>
            <select
              className="mx-1 form-select"
              name="aqms"
              id="aqms"
              onChange={(e) => setSelectedAQMS(e.target.value)}
            >
              <option value="all">-</option>
              <option value="yes">✔</option>
              <option value="no">X</option>
            </select>
          </div>

          <div className="col-auto d-flex align-items-center justify-content-center my-auto">
            <p> تعداد دستگاه های انتخاب شده : {currentPageData.length}</p>
          </div>
        </div>

        <div className="row">
          <div className="table-responsive border-top border-dark">
            <table className="table table-light table-striped">
              <thead>
                <tr className="text-center">
                  <th className="col-1">دستگاه</th>
                  <th className="col-1">نام تجاری</th>
                  <th className="col-1">سریال</th>
                  <th className="col-1">استان</th>
                  <th className="col-1">شهر</th>
                  <th className="col-3">نام ایستگاه</th>
                  <th className="col-1">وضعیت</th>
                  <th className="col-1">آخرین داده</th>
                  <th className="col-1">OPM</th>
                  <th className="col-1">AQMS</th>
                  <th className="col-1">داده ها </th>
                </tr>
              </thead>
              <tbody>
                {currentPageData && currentPageData.length > 0 ? (
                  currentPageData.map((stationInfo) => (
                    <tr key={stationInfo.slug} className="text-center">
                      <td>{stationInfo.device}</td>
                      <td>
                        {stationInfo.device === "OSPM"
                          ? "SAPA A"
                          : stationInfo.device === "SAPA" &&
                            stationInfo.is_kit !== true
                          ? "SAPA B"
                          : (stationInfo.device === "SAPA" &&
                              stationInfo.is_kit === true) ||
                            stationInfo.device === "SATA"
                          ? "SAPA C"
                          : stationInfo.device === "ESPS"
                          ? "ESPS"
                          : stationInfo.device === "OSPL"
                          ? "OSPL"
                          : stationInfo.device === "OPSGA"
                          ? "OPSGA"
                          : stationInfo.device === "OPEDA"
                          ? "OPEDA"
                          : stationInfo.device === "SATA2"
                          ? "SATA2"
                          : ""}
                      </td>
                      <td>{stationInfo.device_id}</td>
                      <td>{stationInfo.province}</td>
                      <td>{stationInfo.city}</td>
                      <td>{stationInfo.stationName}</td>

                      {stationInfo.ccreated_datetime ? (
                        <td
                          className={`text-center text-large align-baseline ${getIconColor(
                            checkOnlineOrOffline(
                              stationInfo.ccreated_datetime,
                              stationInfo.PM25AQI,
                              stationInfo.PM10AQI
                            )
                          )}`}
                        >
                          <RiRadioButtonLine />
                        </td>
                      ) : stationInfo.created_datetime ? (
                        <td
                          className={`text-center text-large align-baseline ${getIconColor(
                            checkOnlineOrOffline(
                              stationInfo.created_datetime,
                              stationInfo.PM25AQI,
                              stationInfo.PM10AQI
                            )
                          )}`}
                        >
                          <RiRadioButtonLine />
                        </td>
                      ) : (
                        ""
                      )}

                      <td>
                        {stationInfo.ccreated_datetime
                          ? convertToJalali(stationInfo.ccreated_datetime)
                          : convertToJalali(stationInfo.created_datetime)}
                      </td>
                      {stationInfo.is_opm ? (
                        <td className="text-center text-large align-baseline">
                          <MdCheckBox />
                        </td>
                      ) : (
                        <td className="text-center text-large align-baseline">
                          <MdCheckBoxOutlineBlank />
                        </td>
                      )}
                      {stationInfo.is_aqms ? (
                        <td className="text-center text-large align-baseline">
                          <MdCheckBox />
                        </td>
                      ) : (
                        <td className="text-center text-large align-baseline">
                          <MdCheckBoxOutlineBlank />
                        </td>
                      )}
                      {stationInfo.device !== "OSPL" &&
                      stationInfo.device !== "OPSGA" &&
                      stationInfo.device !== "OPEDA" &&
                      stationInfo.device !== "SATA2" ? (
                        <td className="text-center px-md-5 align-baseline">
                          <GoToStationButton theRouteUrl={stationInfo.slug} />
                        </td>
                      ) : stationInfo.device === "OSPL" ? (
                        <td className="text-center px-md-5">
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() =>
                              navigateToSoundHistory(stationInfo.slug)
                            }
                          >
                            اطلاعات ایستگاه
                          </button>
                        </td>
                      ) : stationInfo.device === "OPSGA" ||
                        stationInfo.device === "OPEDA" ? (
                        <td className="text-center px-md-5 align-baseline">
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() =>
                              navigateToStackHistory(stationInfo.slug)
                            }
                          >
                            اطلاعات ایستگاه
                          </button>
                        </td>
                      ) : stationInfo.device === "SATA2" ? (
                        <td className="text-center px-md-5 align-baseline">
                          <button
                            className="btn btn-dark btn-sm"
                            onClick={() =>
                              navigateToWeatherHistory(stationInfo.slug)
                            }
                          >
                            اطلاعات ایستگاه
                          </button>
                        </td>
                      ) : (
                        ""
                      )}
                    </tr>
                  ))
                ) : (
                  <></>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
};

export default Information;
